import { DeleteOutlined, EditFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Switch, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchRegionRecords,
    insertRegionRecord,
    updateRegionRecord,
    deleteRegionRecord,
    changeRegionParameter,
} from '../../../redux/slice/region.slice';
import moment from 'moment';
import DebouncedSearch from '../../Common/DebouncedSearch';
import CommonTable from '../../Common/AdvancedTable';

const inputClass = `form-control
block
w-full
px-3
py-1.5
text-base
font-normal
text-gray-700
bg-white bg-clip-padding
border border-solid border-gray-300
rounded
transition
ease-in-out
m-0
focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none`;

export default function RegionMaster() {
    const Region = useSelector((state) => state.region);
    const dispatch = useDispatch();

    const [pagination, setPagination] = React.useState({
        pageNo: 0,
        limit: 15,
        count: 0,
        sortBy: 1,
        sortField: '_id',
        keywords: '',
    });

    useEffect(() => {
        setPagination({
            pageNo: Region.pageNo || 0,
            limit: Region.limit || 15,
            count: Region.count || 0,
            sortBy: Region.sortBy || 1,
            sortField: Region.sortField || '_id',
            keywords: Region.keywords || '',
        });
        fetchRecord({
            pageNo: Region.pageNo || 0,
            limit: Region.limit || 15,
            sortBy: Region.sortBy || 1,
            sortField: Region.sortField || '_id',
            keywords: Region.keywords || '',
        });
    }, []);

    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            count: Region.count || 0,
        }));
    }, [Region.count]);

    function fetchRecord({ pageNo, limit, sortBy, sortField, keywords }) {
        dispatch(fetchRegionRecords({ pageNo, limit, sortBy, sortField, keywords }));
    }

    const handleDeleteSlide = (id) => {
        dispatch(deleteRegionRecord({ id }));
        setTimeout(() => {
            fetchRecord(pagination);
        }, 100);
    };

    const handleSearch = (keywords) => {
        const updatedPagination = { ...pagination, pageNo: 0, keywords };
        setPagination(updatedPagination);
        dispatch(changeRegionParameter(updatedPagination));
        dispatch(fetchRegionRecords(updatedPagination));
    };

    const handleRegionSubmit = async (data, id, setOpen) => {
        try {
            if (id) {
                data._id = id;
                await dispatch(updateRegionRecord(data));
            } else {
                await dispatch(insertRegionRecord(data));
            }

            setTimeout(() => {
                dispatch(fetchRegionRecords(pagination));
            }, 100);
            setOpen(() => false);
        } catch (err) {
            alert(err.message);
        }
    };

    const onChangeTable = (paginate, filter, sorter, extra) => {
        const updatedSortField = sorter.field || '_id';
        const updatedSortBy = sorter.order === 'ascend' ? 1 : -1;

        const updatedPagination = {
            ...pagination,
            pageNo: paginate.current - 1,
            limit: paginate.pageSize,
            sortBy: updatedSortBy,
            sortField: updatedSortField,
            keywords: pagination.keywords,
        };

        setPagination(updatedPagination);
        dispatch(changeRegionParameter(updatedPagination));
        dispatch(fetchRegionRecords(updatedPagination));
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            sorter: (a, b) => a.name?.localeCompare(b.name),
        },
        {
            title: 'Tender Title',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            ellipsis: true,
            sorter: (a, b) => a?.title?.localeCompare(b?.title),
        },
        {
            title: 'Tender Description',
            dataIndex: 'description',
            key: 'description',
            width: 250,
            ellipsis: true,
            sorter: (a, b) => a?.description?.localeCompare(b?.description),
        },
        {
            title: 'Project Title',
            dataIndex: 'project_title',
            key: 'project_title',
            width: 200,
            ellipsis: true,
            sorter: (a, b) => a?.project_title?.localeCompare(b?.project_title),
        },
        {
            title: 'Project Description',
            dataIndex: 'project_description',
            key: 'project_description',
            width: 250,
            ellipsis: true,
            sorter: (a, b) =>
                a?.project_description?.localeCompare(b?.project_description),
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 120,
            sorter: (a, b) => a.code?.localeCompare(b.code),
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (e, record) => moment(e).format('lll'),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        },
        {
            title: 'Action',
            dataIndex: 'Action',
            key: 'Action',
            width: 150,
            fixed: 'right',
            render: (e, record) => (
                <div className="flex gap-2" key={record._id}>
                    <ManagerModel
                        key={record._id}
                        id={record._id}
                        submitHandler={handleRegionSubmit}
                        _region={record}
                    />
                    <Button
                        title="Delete"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteSlide(record._id)}
                    ></Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl">Regions</h1>
                <div className="flex gap-2 ml-auto">
                    <DebouncedSearch
                        onSearch={handleSearch}
                        placeholder="Search Regions..."
                    />
                    <ManagerModel submitHandler={handleRegionSubmit} />
                </div>
            </div>
            <CommonTable
                data={Region.records}
                columns={columns}
                loading={Region.loading}
                pagination={pagination}
                onChange={onChangeTable}
            />
        </div>
    );
}

function ManagerModel({ id, _region, submitHandler }) {
    const [open, setOpen] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm();

    const onSubmit = (body) => {
        submitHandler(body, id, setOpen);
    };

    return (
        <div className="">
            <Button
                className={!id ? 'float-right mb-2' : ''}
                title={id ? 'Edit' : 'Add'}
                icon={id ? <EditFilled /> : <PlusOutlined />}
                onClick={() => setOpen(true)}
            >
                {id ? '' : 'Add'}
            </Button>

            <Modal
                centered
                title={(id ? 'Edit' : 'Add') + ' Region'}
                open={open}
                onOk={() => setOpen(false)}
                footer={false}
                onCancel={() => setOpen(false)}
            >
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Name <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.name?.message}
                            </span>
                            <input
                                type="text"
                                className={inputClass}
                                name="name"
                                {...register('name', { required: 'field is required' })}
                                aria-describedby="name"
                                placeholder="name"
                                defaultValue={_region?.name || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Tender Title <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.title?.message}
                            </span>
                            <input
                                type="text"
                                className={inputClass}
                                name="title"
                                {...register('title', { required: 'field is required' })}
                                aria-describedby="description"
                                placeholder="tender title"
                                defaultValue={_region?.title || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Tender Description <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.description?.message}
                            </span>
                            <textarea
                                type="text"
                                className={inputClass}
                                name="description"
                                {...register('description', {
                                    required: 'field is required',
                                })}
                                aria-describedby="description"
                                placeholder="tender description"
                                defaultValue={_region?.description || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Project Title <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.title?.message}
                            </span>
                            <input
                                type="text"
                                className={inputClass}
                                name="project_title"
                                {...register('project_title', {
                                    required: 'field is required',
                                })}
                                aria-describedby="description"
                                placeholder="project title"
                                defaultValue={_region?.project_title || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Project Description{' '}
                                <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.description?.message}
                            </span>
                            <textarea
                                type="text"
                                className={inputClass}
                                name="project_description"
                                {...register('project_description', {
                                    required: 'field is required',
                                })}
                                aria-describedby="description"
                                placeholder="project description"
                                defaultValue={_region?.project_description || ''}
                            />
                        </div>
                        <div className="form-group mb-6">
                            <label className="font-bold">
                                Code <span className="text-red-600">*</span>
                            </label>
                            <span className="text-red-600 md:ml-4">
                                {errors?.code?.message}
                            </span>
                            <input
                                type="text"
                                className={inputClass}
                                name="code"
                                {...register('code', { required: 'field is required' })}
                                aria-describedby="code"
                                placeholder="code"
                                defaultValue={_region?.code || ''}
                            />
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="px-4 py-2 rounded shadow hover:bg-gray-200"
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}
